import { authToken } from "@infrastructure/clients/http/authToken";

export interface SubscriptionOptions {
  onMessage: (data: any) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  reconnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

export abstract class SSEClient {
  abstract baseUrl: string;
  private eventSource: EventSource | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private currentEndpoint: string | null = null;

  subscribe(endpoint: string, options: SubscriptionOptions): () => void {
    const fullUrl = `${this.baseUrl}${endpoint}`;
    
    // Добавляем токен авторизации в URL (SSE не поддерживает кастомные заголовки)
    const token = authToken.get();
    const urlWithAuth = token 
      ? `${fullUrl}?token=${encodeURIComponent(token)}`
      : fullUrl;

    this.currentEndpoint = endpoint;
    this.eventSource = new EventSource(urlWithAuth);

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        options.onMessage(data);
      } catch (error) {
        options.onError?.(error as Error);
      }
    };

    this.eventSource.onerror = (error) => {
      options.onError?.(error as any);
      
      if (options.reconnect !== false && this.eventSource?.readyState === EventSource.CLOSED) {
        const delay = options.reconnectDelay || 1000;
        const maxAttempts = options.maxReconnectAttempts || 5;
        
        if (this.reconnectAttempts < maxAttempts) {
          this.reconnectAttempts++;
          this.reconnectTimer = setTimeout(() => {
            this.subscribe(endpoint, options);
          }, delay * this.reconnectAttempts);
        } else {
          console.error("[SSE] Max reconnection attempts reached");
        }
      }
    };

    this.eventSource.addEventListener("close", () => {
      options.onClose?.();
    });

    return () => {
      this.stop();
    };
  }

  stop(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.eventSource?.close();
    this.eventSource = null;
    this.reconnectAttempts = 0;
    this.currentEndpoint = null;
  }

  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }
}

